import React, { useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
  ColumnSort,
  TextOverflow,
  TableHead,
  Checkbox,
  CardContent,
  Card,
  Search,
  Button,
  CardHeader,
  ColumnResize,
} from "@forge/common";
import Title from "../title-container/title"; // Adjust the path as needed
import { TableHeaderText, BodyText } from "../title-container/LText"; // Adjust the path as needed
import { ICustomizeViewColumns } from "../../../iso-file-management/models/isoFileManagment.model"; // Adjust the path as needed
import { ISO_FILE_MANAGEMENT_CONSTANTS } from "../../../iso-file-management/utils/constant";
import "./tableContainer.scss";
import useResponsiveSize from "../../utils/useWindowSize";

interface TableContainerProps {
  column?: ICustomizeViewColumns[];
  data?: any[];
  btnText?: string;
  onClick?: () => void;
  onSearchFunction?: (value: string) => void;
  searchValue?: string;
  btnClassName?: string;
  slotRight?: React.ReactNode;
  slotLeft?: React.ReactNode;
  maxHeight?: string;
  PressEnter?: (event: React.KeyboardEvent<HTMLInputElement>) => void; // Function to handle Enter key press
  hidePageCount?: boolean;
  totalRecords?: string;
  searchPlaceHolder?: string;
}

interface SearchContainerProps {
  value?: string;
  onSearchFunction?: (value: string) => void;
  totalCount?: number; // Optional total count prop for displaying total items
  PressEnter?: (event: React.KeyboardEvent<HTMLInputElement>) => void; // Function to handle Enter key press
  hidePageCount?: boolean;
  totalRecords?: string;
  searchPlaceHolder?: string;
}

const SearchContainer: React.FC<SearchContainerProps> = ({
  value,
  onSearchFunction,
  totalCount,
  PressEnter,
  hidePageCount,
  totalRecords,
  searchPlaceHolder,
}) => {
  const focus = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (PressEnter) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (
          event.key === "Enter" &&
          focus.current &&
          document.activeElement === focus.current &&
          (event.target as HTMLInputElement)?.value?.length > 0
        ) {
          // You may need to cast to the correct type if your handler expects a KeyboardEvent<HTMLInputElement>
          PressEnter(event as unknown as React.KeyboardEvent<HTMLInputElement>);
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [PressEnter]);

  return (
    <div className="flexContainer alignItems-center gap-rem table-Container">
      <Search
        ref={focus}
        id="search-iso-file"
        data-testid="search-iso-file"
        className="search-bar"
        minCharacters={1}
        onChange={(e: any) => {
          onSearchFunction && onSearchFunction(e.target.value);
        }}
        placeholder={
          searchPlaceHolder || ISO_FILE_MANAGEMENT_CONSTANTS.SEARCH_THE_NAME
        }
        size={useResponsiveSize() === "regular" ? "regular" : "large"}
      />
      {hidePageCount ? null : (
        <BodyText
          title={`${totalCount ? "1" : "0"}-${totalCount || 0} of ${
            totalRecords || "N/A"
          } total`}
          className="text-[#D0D0D0] ml-3"
        />
      )}
    </div>
  );
};

const TableContainer: React.FC<TableContainerProps> = ({
  column,
  data,
  onClick,
  btnText,
  onSearchFunction,
  searchValue,
  btnClassName,
  slotRight,
  slotLeft,
  maxHeight,
  PressEnter,
  hidePageCount,
  totalRecords,
  searchPlaceHolder,
}) => {
  return (
    <Card className="mx-auto w-[25rem] min-w-full max-w-4xl">
      <CardHeader
        className="padding-10"
        slotRight={
          slotRight ? (
            <>{slotRight}</>
          ) : onClick ? (
            <Button
              className={`font-12 delete-button ${btnClassName}`}
              onClick={onClick}
              size="regular"
            >
              {btnText}
            </Button>
          ) : null
        }
      >
        <div className="flex items-center gap-2">
          {slotLeft ? slotLeft : null}
          {onSearchFunction && (
            <SearchContainer
              value={searchValue}
              onSearchFunction={onSearchFunction}
              totalCount={data?.length}
              totalRecords={totalRecords}
              PressEnter={PressEnter}
              hidePageCount={hidePageCount}
              searchPlaceHolder={searchPlaceHolder}
            />
          )}
        </div>
      </CardHeader>
      <div
        className={`h-[${maxHeight ? maxHeight : "calc(100vh-250px)"
          }] overflow-auto`}
      >
        <CardContent x="none" top="none" className="!pb-0">
          <Table fixedFirstColumn onCard>
            <TableHead>
              <TableRow>
                {column?.map((col: any, index: number) => (
                  <TableHeaderCell
                    id={col.id}
                    key={index}
                    className={`${col.className}`}
                  >
                    {col.resize && <ColumnResize key={index} column={col.id} />}
                    {col.checkbox ? (
                      <Checkbox
                        className="h-[30px] w-[30px]"
                        id="select-all"
                        data-testid={col.datatestId}
                        onChange={(e) => col.onChange(e)}
                        checked={col.checked}
                      />
                    ) : (
                      <div className="justify-between flexContainer alignItems-center">
                        <TableHeaderText title={col?.name} />
                        {col?.onSort && (
                          <ColumnSort
                            isVisible
                            sort={col?.sort || "desc"}
                            onSort={col?.onSort}
                          />
                        )}
                      </div>
                    )}
                  </TableHeaderCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data && data.length > 0 ? (
                data.map((item: any, index: number) => (
                  <TableRow
                    className="table-row mt-1 mb-1 cursor-pointer xl-high:h-[50px] self-end items-center content-center"
                    id={`table-${index}`}
                    key={index}
                  >
                    {column?.map((col: any, i: number) => (
                      <TableCell
                        id={`id_${i}`}
                        key={i}
                        className={`font-16 xl-high:h-[50px] self-end items-center content-center py-2`}
                        title={col?.render ? null : item[col.key]}
                      >
                        {col?.render ? (
                          col.render(item, index)
                        ) : (
                          <BodyText
                            title={item[col.key]}
                            className={`truncate font-normal text-[#45A7EA] xl-high:!leading-[30px]`}
                          />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={column?.length || 1}>
                    <div className="flex justify-center items-center w-full h-[50px]">
                      <BodyText
                        title="No records found."
                        className="text-center"
                      />
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </div>
    </Card>
  );
};

export default TableContainer;
